// 성장 특성 DOM: 정비 서랍은 drawCompact(15개 노드 목록)와 drawActiveDetail(선택행 강화)을 사용한다.
// 구형 draw는 호환용이며 현재 가방·교관 진입은 skillDrawer.ts가 담당한다.
// 원본 learnSkill·resetSkills·setSkillFocus 명령과 요청 ID를 보존한다.
import { mountSkillArt } from './skillArt';
import { SKILL_BTN_ALL } from '../shared/types';
import { SKILL_ICONS } from './skillbar';
import { BRANCHES, NODE_COUNT, TIER_LEVELS, type BranchDef } from '../shared/growth';
import type { GameCommand, GameView } from '../shared/types';
import { FONT, FONT_NUM, el, hudUrl, portraitImage } from './dom';
import { FOCUS_BONUS_TEXT, makeRequestId } from './rpgModel';
import { LOCK_SVG, STAR_SVG, emblemGlyph, emblemUrl, layeredImg, nodeGlyph, nodeIconCandidates } from './skillTreeArt';
import { branchDef, branchNodes, defaultNode, growthOf, nodeCard, treeHeader, treeNodes, type NodeCard, type TreeNode } from './skillTreeModel';

const STYLE_ID = 'dh-st-style';
const CSS =
  '.dh-st{--bh:76px;display:flex;flex-direction:column;gap:10px;min-height:0;font-family:' + FONT + '}' +
  '@keyframes dhStPulse{0%,100%{box-shadow:0 0 0 2px rgba(255,236,170,.55),0 0 8px 1px var(--bc)}50%{box-shadow:0 0 0 3px rgba(255,236,170,.95),0 0 22px 6px var(--bc)}}' +
  '@media (prefers-reduced-motion:reduce){.dh-st-node[data-l="1"] .dh-st-ic{animation:none!important;box-shadow:0 0 0 3px rgba(255,236,170,.9),0 0 14px 3px var(--bc)!important}}' +
  // 머리
  '.dh-st-head{display:flex;align-items:center;gap:18px;padding:8px 16px;border-radius:10px;border:1.5px solid rgba(242,193,78,.4);background:linear-gradient(180deg,rgba(18,26,52,.85),rgba(10,15,32,.85));box-shadow:inset 0 1px 0 rgba(255,255,255,.06)}' +
  '.dh-st-title{display:flex;align-items:center;gap:12px;min-width:0}' +
  '.dh-st-title svg{width:36px;height:36px;color:var(--g2);filter:drop-shadow(0 0 6px rgba(242,193,78,.5))}' +
  '.dh-st-title b{display:block;font:900 28px ' + FONT + ';color:#fff;letter-spacing:1px;text-shadow:0 2px 6px rgba(0,0,0,.8);white-space:nowrap}' +
  '.dh-st-title span{display:block;font:600 13px ' + FONT + ';color:var(--mut);margin-top:1px}' +
  '.dh-st-hero{display:flex;align-items:center;gap:10px;margin-left:auto;min-width:0}' +
  '.dh-st-face{position:relative;flex:0 0 auto;width:54px;height:54px;border-radius:50%;overflow:hidden;border:2.5px solid var(--g);box-shadow:0 0 0 3px rgba(9,12,26,.92),0 0 12px rgba(242,193,78,.4);background:radial-gradient(circle at 35% 30%,#f5f7ff,#9aa6c8 55%,#4a567a)}' +
  '.dh-st-face img{position:absolute;inset:0;width:100%;height:100%;object-fit:cover}' +
  '.dh-st-who b{display:block;font:900 15px ' + FONT + ';color:#fff;line-height:1.1}' +
  '.dh-st-lv{font:900 26px ' + FONT_NUM + ';color:var(--g2);line-height:1.05;text-shadow:0 2px 0 rgba(0,0,0,.6)}' +
  '.dh-st-xp{width:200px;height:9px;margin-top:3px;border-radius:5px;background:rgba(0,0,0,.55);box-shadow:inset 0 0 0 1px rgba(255,255,255,.14);overflow:hidden}' +
  '.dh-st-xp i{display:block;height:100%;border-radius:5px;background:linear-gradient(90deg,#c88a1a,#ffe07a);box-shadow:0 0 8px rgba(255,214,90,.6)}' +
  '.dh-st-xpt{margin-top:2px;font:700 11px ' + FONT + ';color:var(--mut);text-align:right;letter-spacing:.3px}' +
  '.dh-st-pts{display:flex;align-items:center;gap:8px;flex:0 0 auto;padding:5px 18px 5px 14px;border-radius:999px;border:1.5px solid rgba(242,193,78,.7);background:linear-gradient(180deg,rgba(8,12,28,.92),rgba(22,30,58,.92));font:800 15px ' + FONT + ';color:#dfe6ff;white-space:nowrap}' +
  '.dh-st-pts svg{color:var(--g2);filter:drop-shadow(0 0 5px rgba(255,215,106,.8))}' +
  '.dh-st-pts b{font:900 28px ' + FONT_NUM + ';color:#fff;min-width:22px;text-align:center}' +
  '.dh-st-pts.zero b{color:#8e98b8}' +
  '.dh-st-note{padding:8px 14px;border-radius:8px;background:linear-gradient(90deg,rgba(255,42,61,.25),rgba(255,42,61,.08));border:1.5px solid rgba(255,42,61,.55);font:700 14px ' + FONT + ';line-height:1.4}' +
  // 본문: 트리 + 카드
  '.dh-st-main{display:grid;grid-template-columns:minmax(0,1fr) 336px;gap:12px;align-items:start;min-height:0}' +
  '.dh-st-tree{display:grid;grid-template-columns:60px repeat(3,minmax(0,1fr));gap:8px}' +
  '.dh-st-lab{display:flex;flex-direction:column}' +
  '.dh-st-lab>div{height:80px;display:flex;align-items:center;justify-content:center;gap:5px;font:800 15px ' + FONT + ';color:#6f7a9c;white-space:nowrap}' +
  '.dh-st-lab>div:first-child{height:var(--bh)}' +
  '.dh-st-lab>div::before,.dh-st-lab>div::after{content:"";width:5px;height:5px;transform:rotate(45deg);background:currentColor;opacity:.6}' +
  '.dh-st-lab>div:first-child::before,.dh-st-lab>div:first-child::after{display:none}' +
  '.dh-st-lab>div.on{color:var(--g2);text-shadow:0 0 8px rgba(255,215,106,.45)}' +
  '.dh-st-col{--bc:#888;display:flex;flex-direction:column;min-width:0;border-radius:10px;border:1.5px solid color-mix(in srgb,var(--bc) 55%,transparent);overflow:hidden;' +
  'background:radial-gradient(120% 60% at 50% 0%,color-mix(in srgb,var(--bc) 24%,transparent),transparent 70%),linear-gradient(180deg,#0e1430,#090d1e)}' +
  '.dh-st-bh{height:var(--bh);box-sizing:border-box;display:flex;align-items:center;gap:8px;padding:6px 8px;border-bottom:1px solid color-mix(in srgb,var(--bc) 40%,transparent);background:linear-gradient(180deg,color-mix(in srgb,var(--bc) 32%,transparent),color-mix(in srgb,var(--bc) 8%,transparent))}' +
  '.dh-st-em{position:relative;flex:0 0 auto;width:46px;height:46px;display:grid;place-items:center;color:var(--bc)}' +
  '.dh-st-em svg{width:34px;height:34px;filter:drop-shadow(0 0 6px var(--bc))}' +
  '.dh-st-em img{position:absolute;inset:0;width:100%;height:100%;object-fit:contain}' +
  '.dh-st-bh b{display:block;font:900 19px ' + FONT + ';color:#fff;text-shadow:0 2px 4px rgba(0,0,0,.8);white-space:nowrap}' +
  '.dh-st-bh p{margin:2px 0 0;font:500 11.5px/1.3 ' + FONT + ';color:#c9d3f0;word-break:keep-all}' +
  // 노드 칸
  '.dh-st-cell{position:relative;height:80px}' +
  '.dh-st-node{position:relative;display:grid;place-items:center;width:100%;height:100%;padding:0;border:0;background:none;cursor:pointer;color:var(--bc);-webkit-tap-highlight-color:transparent}' +
  '.dh-st-node::before,.dh-st-node::after{content:"";position:absolute;left:50%;width:3px;margin-left:-1.5px;background:#2c3556;pointer-events:none}' +
  '.dh-st-node::before{top:0;height:13px}.dh-st-node::after{bottom:0;height:13px}' +
  '.dh-st-node[data-top="1"]::before,.dh-st-node[data-bot="1"]::after{display:none}' +
  '.dh-st-node[data-up="1"]::before,.dh-st-node[data-dn="1"]::after{background:linear-gradient(180deg,#ffe597,#d4922a);box-shadow:0 0 6px rgba(242,193,78,.8)}' +
  '.dh-st-ic{position:relative;width:54px;height:54px;box-sizing:border-box;border-radius:50%;border:3px solid #4a5370;display:grid;place-items:center;' +
  'background:radial-gradient(circle at 38% 30%,color-mix(in srgb,var(--bc) 30%,#2a3354),#0b0f20 78%);box-shadow:inset 0 0 10px rgba(0,0,0,.6),0 3px 6px rgba(0,0,0,.5);z-index:1}' +
  '.dh-st-ic>img,.dh-st-ic .g{position:absolute;pointer-events:none}' +
  '.dh-st-ic>img{inset:0;width:100%;height:100%;border-radius:50%;object-fit:cover}' +
  '.dh-st-ic .g{left:20%;top:20%;width:60%;height:60%;color:var(--bc);filter:drop-shadow(0 0 4px var(--bc))}' +
  '.dh-st-ic .gs{position:absolute;inset:14%}' +
  '.dh-st-node[data-s="learned"] .dh-st-ic,.dh-st-node[data-s="maxed"] .dh-st-ic{border-color:#ffd76a;box-shadow:inset 0 0 8px rgba(0,0,0,.5),0 0 0 2px rgba(255,215,106,.22),0 0 16px 2px var(--bc)}' +
  '.dh-st-node[data-s="maxed"] .dh-st-ic{border-color:#fff1b8;box-shadow:inset 0 0 8px rgba(0,0,0,.4),0 0 0 3px rgba(255,236,170,.4),0 0 20px 4px var(--bc)}' +
  '.dh-st-node[data-s="open"] .dh-st-ic{border-color:color-mix(in srgb,var(--bc) 60%,#4a5370)}' +
  '.dh-st-node[data-s="open"] .dh-st-ic>img,.dh-st-node[data-s="open"] .dh-st-ic .g{opacity:.7}' +
  '.dh-st-node[data-s="locked"] .dh-st-ic{border-color:#3a4260;filter:grayscale(.85) brightness(.62)}' +
  '.dh-st-node[data-l="1"] .dh-st-ic{animation:dhStPulse 1.8s ease-in-out infinite}' +
  '.dh-st-node[aria-pressed="true"] .dh-st-ic{outline:3px solid #fff;outline-offset:3px}' +
  '.dh-rpg .dh-st-node:focus-visible{outline:none;box-shadow:none!important}' +
  '.dh-rpg .dh-st-node:focus-visible .dh-st-ic{outline:3px solid #fff;outline-offset:4px}' +
  '.dh-st-pill{position:absolute;left:calc(50% + 12px);bottom:7px;z-index:2;display:flex;align-items:center;gap:3px;padding:1px 7px;border-radius:9px;background:rgba(8,10,22,.92);border:1px solid rgba(255,255,255,.22);font:800 12px ' + FONT_NUM + ';color:#fff;white-space:nowrap;pointer-events:none;letter-spacing:.3px}' +
  '.dh-st-pill.lock{color:#9aa4c4;font-family:' + FONT + '}' +
  '.dh-st-pill.max{color:#ffe597;border-color:rgba(255,215,106,.6)}' +
  '.dh-st-pill.zero{color:#aeb9da}' +
  '.dh-st-focus{position:absolute;left:calc(50% - 38px);top:5px;z-index:2;padding:0 6px;border-radius:7px;background:linear-gradient(180deg,#e0303f,#9d1020);color:#ffe7a0;font:900 10px/15px ' + FONT + ';box-shadow:0 1px 3px rgba(0,0,0,.6);pointer-events:none}' +
  '.dh-st-cap{position:absolute;left:calc(50% - 38px);top:5px;z-index:2;padding:0 5px;border-radius:7px;background:rgba(8,10,22,.9);border:1px solid rgba(255,215,106,.5);color:var(--g2);font:900 10px/14px ' + FONT + ';pointer-events:none}' +
  // 카드
  '.dh-st-card{display:flex;flex-direction:column;gap:9px;padding:12px 14px;border-radius:10px;border:1.5px solid rgba(242,193,78,.45);background:linear-gradient(180deg,rgba(26,36,68,.95),rgba(10,15,32,.95));box-shadow:inset 0 1px 0 rgba(255,255,255,.07),0 4px 14px rgba(0,0,0,.3)}' +
  '.dh-st-ch{display:flex;align-items:center;gap:12px}' +
  '.dh-st-ch .dh-st-ic{flex:0 0 auto;width:62px;height:62px;border-width:3px}' +
  '.dh-st-ch b{display:block;font:900 22px ' + FONT + ';color:#fff;line-height:1.15}' +
  '.dh-st-ch em{display:block;font:900 20px ' + FONT_NUM + ';font-style:normal;color:var(--g2);margin-top:2px}' +
  '.dh-st-tags{display:flex;gap:6px;flex-wrap:wrap;margin-top:3px}' +
  '.dh-st-tag{padding:0 8px;border-radius:5px;font:900 11px/17px ' + FONT + ';border:1.5px solid currentColor;color:#7fd3e6;white-space:nowrap}' +
  '.dh-st-desc{margin:0;font:500 13.5px/1.5 ' + FONT + ';color:#c9d3f0;word-break:keep-all}' +
  '.dh-st-bonus{display:flex;align-items:center;gap:6px;padding:4px 10px;border-radius:7px;background:rgba(0,0,0,.28);border:1px solid rgba(242,193,78,.3);font:800 12.5px ' + FONT + ';color:#aeb9da}' +
  '.dh-st-bonus.on{color:#ffe597;border-color:rgba(255,215,106,.7)}' +
  '.dh-st-rows{display:flex;flex-direction:column;border-radius:7px;background:rgba(0,0,0,.28);border:1px solid rgba(242,193,78,.18)}' +
  '.dh-st-row{display:grid;grid-template-columns:minmax(0,1fr) auto 18px auto;align-items:center;gap:6px;padding:5px 10px;font:700 14px ' + FONT + ';color:#dfe6ff}' +
  '.dh-st-row+.dh-st-row{border-top:1px solid rgba(242,193,78,.16)}' +
  '.dh-st-row b{font:900 15px ' + FONT + ';color:#fff;text-align:right;white-space:nowrap}' +
  '.dh-st-row i{font-style:normal;text-align:center;color:var(--g)}.dh-st-row i.same{color:#59627f}' +
  '.dh-st-row b.to.up{color:#ffe597}' +
  '.dh-st-need{font:700 12.5px ' + FONT + ';color:var(--mut)}' +
  '.dh-st-deny{min-height:18px;font:800 13.5px/1.35 ' + FONT + ';color:#ff8a96;text-align:center}' +
  '.dh-st-deny.max{color:#ffe597}' +
  '.dh-st-deny:empty{display:none}' +
  '.dh-st-learn{width:100%;font-size:21px;letter-spacing:1px;display:flex;align-items:center;justify-content:center;gap:8px}' +
  '.dh-st-learn svg{width:18px;height:18px}' +
  '.dh-st-sub{display:flex;gap:8px;align-items:center}' +
  '.dh-st-sub .dh-r-btn{flex:1 1 0;min-width:0;font-size:15px;padding:6px 10px}' +
  '.dh-st-confirm{display:flex;flex-direction:column;gap:6px;padding:8px 10px;border-radius:8px;border:1.5px solid rgba(255,231,160,.5);background:rgba(255,231,160,.06);font:700 13px/1.4 ' + FONT + ';color:#ffe7a0}' +
  '.dh-st-confirm .dh-st-sub{margin-top:2px}' +
  // 낮은 가로 화면(폰 가로, 배율 0.75 미만): 카드가 한 화면에 들어오게 여백·보조 줄을 줄인다
  '.dh-r-win.tight .dh-st-card{gap:5px;padding:8px 12px}.dh-r-win.tight .dh-st-ch .dh-st-ic{width:50px;height:50px}.dh-r-win.tight .dh-st-ch b{font-size:19px}.dh-r-win.tight .dh-st-ch em{font-size:17px;margin-top:0}' +
  '.dh-r-win.tight .dh-st-tags{display:none}.dh-r-win.tight .dh-st-desc{font-size:12px;line-height:1.35}.dh-r-win.tight .dh-st-bonus{padding:2px 8px;font-size:11.5px}' +
  '.dh-r-win.tight .dh-st-row{padding:3px 10px;font-size:13px}.dh-r-win.tight .dh-st-need{font-size:11.5px}.dh-r-win.tight .dh-st-deny{font-size:12px}' +
  '.dh-r-win.tight .dh-st-learn{font-size:18px}.dh-r-win.tight .dh-st-head{padding:5px 14px}.dh-r-win.tight .dh-st{gap:6px}' +
  // 세로 화면: 한 칸으로 쌓는다
  '.dh-r-win.narrow .dh-st{--bh:64px}' +
  '.dh-r-win.narrow .dh-st-head{flex-wrap:wrap;gap:8px 12px;padding:8px 10px}' +
  '.dh-r-win.narrow .dh-st-title b{font-size:22px}.dh-r-win.narrow .dh-st-title span{display:none}' +
  '.dh-r-win.narrow .dh-st-hero{margin-left:0;flex:1 1 auto}.dh-r-win.narrow .dh-st-xp{width:110px}' +
  '.dh-r-win.narrow .dh-st-main{grid-template-columns:minmax(0,1fr)}' +
  '.dh-r-win.narrow .dh-st-tree{grid-template-columns:36px repeat(3,minmax(0,1fr));gap:5px}' +
  '.dh-r-win.narrow .dh-st-lab>div{font-size:11px;gap:2px}.dh-r-win.narrow .dh-st-lab>div::before,.dh-r-win.narrow .dh-st-lab>div::after{display:none}' +
  '.dh-r-win.narrow .dh-st-bh{flex-direction:column;justify-content:center;gap:2px;text-align:center;padding:4px}.dh-r-win.narrow .dh-st-bh p{display:none}' +
  '.dh-r-win.narrow .dh-st-bh b{font-size:15px}.dh-r-win.narrow .dh-st-em{width:34px;height:34px}.dh-r-win.narrow .dh-st-em svg{width:26px;height:26px}' +
  '.dh-r-win.narrow .dh-st-ic{width:48px;height:48px}.dh-r-win.narrow .dh-st-pill{left:calc(50% + 8px);font-size:11px;padding:1px 5px}' +
  '.dh-r-win.narrow .dh-st-focus,.dh-r-win.narrow .dh-st-cap{left:calc(50% - 30px)}';

function ensureStyle(): void {
  if (document.getElementById(STYLE_ID)) return;
  const st = document.createElement('style');
  st.id = STYLE_ID;
  st.textContent = CSS;
  document.head.appendChild(st);
}

export interface SkillTreeEnv {
  readonly view: GameView;
  readonly send: (c: GameCommand) => void;
  /** 교관 창의 안내 한 줄(없으면 기본 부제) */
  readonly subtitle?: string;
  /** 거래 잠금(프로필 모드가 ok가 아님): 주력 변경을 막는다. 성장 잠금(growth.mode)은 따로 본다 */
  readonly locked: boolean;
}

const GROWTH_MODE_TEXT: Record<string, string> = {
  corrupt: '성장 기록을 읽지 못해 스킬 배우기와 초기화를 잠갔어요.',
  future: '더 새로운 버전에서 저장한 성장 기록이에요. 이 화면에서는 건드리지 않고 쉬어요.',
  unavailable: '이 기기에서는 저장 공간을 쓸 수 없어 스킬을 배울 수 없어요.',
};

export class SkillTreeUi {
  /** 선택·확인 상태가 바뀔 때마다 오른다(호출자가 값이 바뀌면 draw를 다시 부른다) */
  version = 0;
  private sel = -1;
  private confirmReset = false;

  /** 창을 다시 열어도 선택 노드는 보존하고 초기화 확인만 접는다 */
  open(): void { this.confirmReset = false; this.version++; }
  get selected(): number { return this.sel; }
  /** 테스트·QA용: 노드를 고른다 */
  select(i: number): void {
    if (i < 0 || i >= NODE_COUNT || i === this.sel) return;
    this.sel = i;
    this.confirmReset = false;
    this.version++;
  }

  draw(parent: HTMLElement, env: SkillTreeEnv): void {
    ensureStyle();
    const g = growthOf(env.view);
    const root = el('div', '', parent);
    root.className = 'dh-st';
    root.dataset.testid = 'skilltree';
    if (!g) {
      el('div', '', root, '성장 정보를 불러오는 중이에요.').className = 'dh-st-note';
      return;
    }
    const nodes = treeNodes(g.ranks, g.level);
    if (this.sel < 0 || this.sel >= NODE_COUNT) this.sel = defaultNode(nodes);
    this.drawHead(root, env);
    if (g.mode !== 'ok') {
      const n = el('div', '', root, '⚠ ' + (GROWTH_MODE_TEXT[g.mode] ?? '성장 기록을 쓸 수 없어요.'));
      n.className = 'dh-st-note';
      n.dataset.testid = 'st-mode-note';
    }
    const main = el('div', '', root);
    main.className = 'dh-st-main';
    this.drawTree(main, nodes, g.level, env);
    this.drawCard(main, nodeCard(g.ranks, g.level, this.sel, env.view.profile.skillFocus), nodes, env);
  }

  /** 서랍의 특성 탭: 15개 노드 모두 같은 목록 스크롤 안에 둔다. */
  drawCompact(parent: HTMLElement, env: SkillTreeEnv): void {
    ensureStyle();
    const g = growthOf(env.view);
    if (!g) { el('p', '', parent, '성장 정보를 불러오는 중이에요.'); return; }
    const nodes = treeNodes(g.ranks, g.level);
    if (this.sel < 0) this.sel = defaultNode(nodes);
    const root = el('div', '', parent);
    root.className = 'dh-st dh-st-compact';
    root.dataset.testid = 'skilltree';
    if (g.mode !== 'ok') el('p', '', root, GROWTH_MODE_TEXT[g.mode] ?? '성장 기록이 잠겨 있어요.').dataset.testid = 'st-mode-note';
    for (const branch of BRANCHES) {
      el('h3', 'margin:8px 0;color:' + branch.color + ';font-size:14px;', root, branch.name);
      for (const n of branchNodes(nodes, branch.id)) {
        const b = el('button', '--bc:' + branch.color + ';', root);
        b.type = 'button';
        b.className = 'dh-trait-row';
        b.dataset.testid = 'st-node-' + n.def.id;
        b.dataset.focusKey = 'node:' + n.def.id;
        b.dataset.s = n.state;
        b.dataset.node = String(n.index);
        b.setAttribute('aria-expanded', String(n.index === this.sel));
        b.setAttribute('aria-pressed', String(n.index === this.sel));
        const ic = el('span', '', b); ic.className = 'dh-st-ic';
        this.fillIcon(ic, n);
        const tx = el('span', 'flex:1;text-align:left;min-width:0;', b);
        el('b', '', tx, n.def.name);
        el('small', 'display:block;margin-top:3px;color:#aeb9da;', tx, n.lockLabel || (n.learnable ? '강화 가능' : n.rank >= n.def.maxRank ? '최고 레벨' : '성장 특성'));
        el('span', '', b, n.rank + '/' + n.def.maxRank);
        b.addEventListener('click', () => this.select(n.index));
        if (n.index === this.sel) this.drawCard(root, nodeCard(g.ranks, g.level, n.index, env.view.profile.skillFocus), nodes, env);
      }
    }
  }

  /** 액티브 선택행 안의 기존 강화·주력·초기화 카드. 특성 탭 선택은 바꾸지 않는다. */
  drawActiveDetail(parent: HTMLElement, env: SkillTreeEnv, node: number): void {
    ensureStyle();
    const g = growthOf(env.view);
    if (!g || node < 0) return;
    this.drawCard(parent, nodeCard(g.ranks, g.level, node, env.view.profile.skillFocus), treeNodes(g.ranks, g.level), env);
  }

  // ── 머리 ──
  private drawHead(root: HTMLElement, env: SkillTreeEnv): void {
    const g = growthOf(env.view)!;
    const h = treeHeader(g);
    const head = el('div', '', root);
    head.className = 'dh-st-head';
    const title = el('div', '', head);
    title.className = 'dh-st-title';
    title.insertAdjacentHTML('beforeend', emblemGlyph('crimson').replace('<svg class="g"', '<svg'));
    const tt = el('div', '', title);
    el('b', '', tt, '스킬 트리');
    el('span', '', tt, env.subtitle ?? '카엘의 전투 스타일을 성장시킵니다.');

    const hero = el('div', '', head);
    hero.className = 'dh-st-hero';
    const face = el('div', '', hero);
    face.className = 'dh-st-face';
    const im = layeredImg([hudUrl(portraitImage(env.view.stats?.armorTier ?? 1))], face);
    if (im) im.dataset.testid = 'st-face';
    const who = el('div', '', hero);
    who.className = 'dh-st-who';
    el('b', '', who, '카엘');
    const lv = el('div', '', who, 'Lv.' + h.level);
    lv.className = 'dh-st-lv';
    lv.dataset.testid = 'st-level';
    const bar = el('div', '', who);
    bar.className = 'dh-st-xp';
    bar.setAttribute('role', 'progressbar');
    bar.setAttribute('aria-valuemin', '0');
    bar.setAttribute('aria-valuemax', '100');
    bar.setAttribute('aria-valuenow', String(Math.round(h.xpFrac * 100)));
    bar.setAttribute('aria-label', '경험치');
    el('i', 'width:' + (h.xpFrac * 100).toFixed(1) + '%;', bar);
    const xt = el('div', '', who, h.xpText);
    xt.className = 'dh-st-xpt';
    xt.dataset.testid = 'st-xp';

    const pts = el('div', '', head);
    pts.className = 'dh-st-pts' + (h.free > 0 ? '' : ' zero');
    pts.dataset.testid = 'st-points';
    pts.setAttribute('aria-label', '스킬 포인트 ' + h.free);
    el('span', '', pts, '스킬 포인트');
    pts.insertAdjacentHTML('beforeend', STAR_SVG);
    el('b', '', pts, String(h.free));
  }

  // ── 트리 ──
  private drawTree(parent: HTMLElement, nodes: readonly TreeNode[], level: number, env: SkillTreeEnv): void {
    const tree = el('div', '', parent);
    tree.className = 'dh-st-tree';
    tree.setAttribute('role', 'group');
    tree.setAttribute('aria-label', '스킬 트리');
    // 층 라벨(위에서 아래로 Lv.20 … Lv.1)
    const lab = el('div', '', tree);
    lab.className = 'dh-st-lab';
    el('div', '', lab, '');
    for (let t = TIER_LEVELS.length - 1; t >= 0; t--) {
      const d = el('div', '', lab, 'Lv.' + TIER_LEVELS[t]);
      if (level >= TIER_LEVELS[t]) d.className = 'on';
    }
    const focus = env.view.profile.skillFocus;
    BRANCHES.forEach((b, bi) => {
      const col = el('div', '--bc:' + b.color + ';', tree);
      col.className = 'dh-st-col';
      col.dataset.branch = b.id;
      this.drawBranchHead(col, b);
      const list = branchNodes(nodes, b.id);
      for (let t = list.length - 1; t >= 0; t--) this.drawNode(col, list[t], bi, t, list, focus);
    });
    tree.addEventListener('keydown', (e) => this.onTreeKey(e, tree));
  }

  private drawBranchHead(col: HTMLElement, b: BranchDef): void {
    const bh = el('div', '', col);
    bh.className = 'dh-st-bh';
    const em = el('div', '', bh);
    em.className = 'dh-st-em';
    em.innerHTML = emblemGlyph(b.id);
    layeredImg([emblemUrl(b.id)], em);
    const tx = el('div', 'min-width:0;', bh);
    el('b', 'color:' + b.color + ';filter:brightness(1.35);', tx, b.name);
    el('p', '', tx, b.desc);
  }

  private drawNode(col: HTMLElement, n: TreeNode, bi: number, t: number, list: readonly TreeNode[], focus: number): void {
    const cell = el('div', '', col);
    cell.className = 'dh-st-cell';
    const btn = el('button', '', cell);
    btn.type = 'button';
    btn.className = 'dh-st-node';
    btn.dataset.testid = 'st-node-' + n.def.id;
    btn.dataset.focusKey = 'node:' + n.def.id;
    btn.dataset.node = String(n.index);
    btn.dataset.col = String(bi);
    btn.dataset.tier = String(t);
    btn.dataset.s = n.state;
    btn.dataset.l = n.learnable ? '1' : '0';
    if (t === list.length - 1) btn.dataset.top = '1';
    if (t === 0) btn.dataset.bot = '1';
    if (n.linkUp) btn.dataset.up = '1';
    if (t > 0 && list[t - 1].linkUp) btn.dataset.dn = '1';
    btn.setAttribute('aria-pressed', n.index === this.sel ? 'true' : 'false');
    const stateText = n.state === 'locked' ? (n.lock === 'level' ? '영웅 레벨 ' + TIER_LEVELS[n.def.tier] + ' 필요' : '아래 단계 기술 필요')
      : n.learnable ? '배울 수 있음' : n.state === 'maxed' ? '최고 레벨' : '';
    btn.setAttribute('aria-label', n.def.name + ' ' + n.rank + '/' + n.def.maxRank + (stateText ? ', ' + stateText : ''));
    btn.addEventListener('click', () => this.select(n.index));

    const ic = el('span', '', btn);
    ic.className = 'dh-st-ic';
    this.fillIcon(ic, n);
    // 랭크 표시(칸 오른쪽 아래): 배운 노드 r/max, 잠긴 노드 자물쇠 + 해금 레벨, 열린 노드 0/max
    const pill = el('span', '', btn);
    if (n.state === 'locked') {
      pill.className = 'dh-st-pill lock';
      pill.innerHTML = LOCK_SVG;
      pill.append(n.lockLabel);
    } else {
      pill.className = 'dh-st-pill' + (n.state === 'maxed' ? ' max' : n.rank === 0 ? ' zero' : '');
      pill.textContent = n.rank + '/' + n.def.maxRank;
    }
    if (n.def.kind === 'capstone') el('span', '', btn, '캡스톤').className = 'dh-st-cap';
    else if (n.def.kind === 'active' && n.def.skillId === focus) {
      const f = el('span', '', btn, '주력');
      f.className = 'dh-st-focus';
      f.dataset.testid = 'rpg-focus-badge';
    }
  }

  /** 노드 아이콘: 이미지가 없으면 가지 색 원 위의 기호가 그대로 보인다(이미지는 그 위에 겹친다) */
  private fillIcon(ic: HTMLElement, n: TreeNode): void {
    if (n.def.kind === 'active') {
      mountSkillArt(ic, 'kael', n.def.skillId, SKILL_ICONS[SKILL_BTN_ALL[n.def.skillId - 1]] ?? '');
      return;
    }
    ic.insertAdjacentHTML('beforeend', nodeGlyph(n.def));
    layeredImg(nodeIconCandidates(n.def.id), ic);
  }

  /** 화살표 키로 노드 사이를 옮긴다(위 = 위층). 포커스는 클릭 없이도 닿는다 */
  private onTreeKey(e: KeyboardEvent, tree: HTMLElement): void {
    const dx = e.key === 'ArrowLeft' ? -1 : e.key === 'ArrowRight' ? 1 : 0;
    const dy = e.key === 'ArrowUp' ? 1 : e.key === 'ArrowDown' ? -1 : 0;
    if (!dx && !dy) return;
    const cur = (e.target as HTMLElement | null)?.closest<HTMLElement>('.dh-st-node');
    if (!cur) return;
    const col = Number(cur.dataset.col) + dx, tier = Number(cur.dataset.tier) + dy;
    const next = tree.querySelector<HTMLElement>('.dh-st-node[data-col="' + col + '"][data-tier="' + tier + '"]');
    if (!next) return;
    e.preventDefault();
    next.focus({ preventScroll: false });
  }

  // ── 선택 노드 카드 ──
  private drawCard(parent: HTMLElement, c: NodeCard, nodes: readonly TreeNode[], env: SkillTreeEnv): void {
    const g = growthOf(env.view)!;
    const n = nodes[c.index];
    const b = branchDef(c.branch);
    const card = el('div', '--bc:' + b.color + ';', parent);
    card.className = 'dh-st-card';
    card.dataset.testid = 'st-card';
    card.dataset.node = c.id;

    const head = el('div', '', card);
    head.className = 'dh-st-ch';
    const ic = el('span', '', head);
    ic.className = 'dh-st-ic';
    ic.style.setProperty('--bc', b.color);
    ic.style.borderColor = n.state === 'locked' ? '#3a4260' : '#ffd76a';
    ic.style.boxShadow = '0 0 14px 1px ' + b.color;
    this.fillIcon(ic, n);
    const tx = el('div', 'min-width:0;', head);
    el('b', '', tx, c.name).dataset.testid = 'st-card-name';
    el('em', '', tx, c.levelText).dataset.testid = 'st-card-level';
    const tags = el('div', '', tx);
    tags.className = 'dh-st-tags';
    el('span', '', tags, c.kindLabel).className = 'dh-st-tag';
    const bt = el('span', 'color:' + b.color + ';filter:brightness(1.3);', tags, b.name);
    bt.className = 'dh-st-tag';

    el('p', '', card, c.desc).className = 'dh-st-desc';
    if (c.bonusName) {
      const bn = el('div', '', card, (c.bonusOn ? '✓ ' : '') + c.maxRank + '레벨 효과 · ' + c.bonusName);
      bn.className = 'dh-st-bonus' + (c.bonusOn ? ' on' : '');
    }
    const rows = el('div', '', card);
    rows.className = 'dh-st-rows';
    rows.dataset.testid = 'st-rows';
    for (const r of c.rows) {
      const row = el('div', '', rows);
      row.className = 'dh-st-row';
      el('span', '', row, r.label);
      el('b', '', row, r.from);
      el('i', '', row, '»').className = r.changed ? '' : 'same';
      const to = el('b', '', row, r.to);
      to.className = 'to' + (r.changed ? ' up' : '');
    }
    if (c.needHeroLevel > 0) el('div', '', card, '다음 스킬 레벨은 영웅 Lv.' + c.needHeroLevel + ' 이상').className = 'dh-st-need';

    const growthOk = g.mode === 'ok';
    const can = c.canLearn && growthOk;
    const learn = el('button', '', card);
    learn.type = 'button';
    learn.className = 'dh-r-btn dh-st-learn' + (can ? ' pri' : '');
    learn.dataset.testid = 'st-learn';
    learn.dataset.focusKey = 'st-learn';
    learn.dataset.sfx = 'event';
    learn.disabled = !can;
    if (c.nextRank === null) learn.textContent = '최고 레벨';
    else {
      learn.append('배우기');
      learn.insertAdjacentHTML('beforeend', STAR_SVG + '<span>1</span>');
    }
    learn.addEventListener('click', () => { if (can) env.send({ type: 'learnSkill', node: c.index, requestId: makeRequestId() }); });
    const deny = el('div', '', card, !growthOk ? '지금은 배울 수 없어요.' : c.denialText);
    deny.className = 'dh-st-deny' + (c.denial === 'max_rank' ? ' max' : '');
    deny.dataset.testid = 'st-deny';

    // 주력 스킬(스킬 1~3): 선택한 액티브 노드에서 바꾼다
    const sub = el('div', '', card);
    sub.className = 'dh-st-sub';
    if (this.confirmReset) sub.style.display = 'none';
    if (c.focusable) {
      const focused = env.view.profile.skillFocus === c.skillId;
      const fb = el('button', '', sub, focused ? '주력 해제' : '주력으로 삼기');
      fb.type = 'button';
      fb.className = 'dh-r-btn' + (focused ? ' held' : '');
      fb.dataset.testid = 'rpg-focus-' + c.skillId;
      fb.dataset.focusKey = 'focus:' + c.skillId;
      fb.dataset.sfx = 'event';
      fb.disabled = env.locked;
      fb.title = FOCUS_BONUS_TEXT;
      // 주력 중이어도 누르면 해제(같은 버튼에서 되돌린다)
      fb.addEventListener('click', () => env.send({ type: 'setSkillFocus', skillId: focused ? 0 : c.skillId, requestId: makeRequestId() }));
      if (focused) fb.disabled = env.locked;
    }
    const reset = el('button', '', sub, '초기화');
    reset.type = 'button';
    reset.className = 'dh-r-btn';
    reset.dataset.testid = 'st-reset';
    reset.dataset.focusKey = 'st-reset';
    reset.disabled = g.spent <= 0 || !growthOk || this.confirmReset;
    reset.addEventListener('click', () => { this.confirmReset = true; this.version++; });

    if (this.confirmReset) {
      const cf = el('div', '', card);
      cf.className = 'dh-st-confirm';
      cf.dataset.testid = 'st-confirm';
      cf.setAttribute('role', 'alertdialog');
      el('div', '', cf, '쓴 스킬 포인트 ' + g.spent + '점을 모두 돌려받아요. 처음부터 가진 기본 스킬은 그대로예요. 비용은 없어요.');
      const row = el('div', '', cf);
      row.className = 'dh-st-sub';
      const ok = el('button', '', row, '네, 초기화');
      ok.type = 'button';
      ok.className = 'dh-r-btn pri';
      ok.dataset.testid = 'st-reset-confirm';
      ok.dataset.focusKey = 'st-reset-confirm';
      ok.dataset.sfx = 'event';
      ok.addEventListener('click', () => {
        this.confirmReset = false;
        this.version++;
        env.send({ type: 'resetSkills', requestId: makeRequestId() });
      });
      const no = el('button', '', row, '취소');
      no.type = 'button';
      no.className = 'dh-r-btn';
      no.dataset.testid = 'st-reset-cancel';
      no.dataset.focusKey = 'st-reset-cancel';
      no.addEventListener('click', () => { this.confirmReset = false; this.version++; });
    }
  }
}
